import apiClient, { ApiRequestError, getApiErrorMessage, handleUnauthorized, readFetchApiError } from './apiClient';
import { getAuthToken } from '../utils/storage';
import { DEFAULT_LLM_MODEL_KEY } from '../constants/llmModels';
import { DEFAULT_LLM_PROVIDER_KEY } from '../constants/llmProviders';
import { extractPreviewFiles, extractSandboxSessionId } from '../utils/previewFiles';
import { retryAsync } from '../utils/retry';

const LLM_REQUEST_MAX_ATTEMPTS = 3;
const LLM_REQUEST_TIMEOUT_MS = 5 * 60 * 1000;
const ATTACHMENT_UPLOAD_MAX_ATTEMPTS = 2;

const createCombinedAbortSignal = (externalSignal, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const abortFromExternal = () => controller.abort();

  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      controller.abort();
    } else {
      externalSignal.addEventListener('abort', abortFromExternal, { once: true });
    }
  }

  const dispose = () => {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener?.('abort', abortFromExternal);
  };

  return { signal: controller.signal, dispose };
};


const unwrapApiData = (data) => data?.data || data?.Data || data;

export const setLlmMessageFeedbackApi = async (messageId, feedback) => {
  try {
    const { data } = await apiClient.put(`/llm/messages/${encodeURIComponent(messageId)}/feedback`, {
      Feedback: feedback
    });
    return unwrapApiData(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to save feedback.'));
  }
};
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);

  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }

  if (Array.isArray(unwrapped?.items)) {
    return unwrapped.items;
  }

  if (Array.isArray(unwrapped?.Items)) {
    return unwrapped.Items;
  }

  if (Array.isArray(unwrapped?.messages)) {
    return unwrapped.messages;
  }

  if (Array.isArray(unwrapped?.Messages)) {
    return unwrapped.Messages;
  }

  return [];
};

const normalizeSuggestions = (value) => {
  if (!value) {
    return [];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === 'string') {
        return item.trim();
      }

      return `${item?.text || item?.Text || item?.label || item?.Label || item?.content || item?.Content || ''}`.trim();
    })
    .filter(Boolean);
};

const normalizeMessageRole = (role) => {
  const normalized = `${role || ''}`.toLowerCase();

  if (normalized === 'user' || normalized === 'assistant' || normalized === 'system') {
    return normalized;
  }

  return normalized.includes('user') ? 'user' : 'assistant';
};

const normalizeImageUrls = (message) => {
  const urls = message?.imageUrls || message?.ImageUrls || [];

  if (!Array.isArray(urls)) {
    return [];
  }

  return urls.map((url) => `${url}`.trim()).filter(Boolean);
};

const normalizeChatMessage = (message, index) => {
  const createdAt = message?.CreatedAt || message?.createdAt || message?.Timestamp || message?.timestamp || null;
  const files = message?.Files || message?.files || null;
  const sandbox = message?.Sandbox || message?.sandbox || null;

  const displayMessage = message?.Message || message?.message || '';

  return {
    id: message?.Id || message?.id || `${message?.ChatId || message?.chatId || 'message'}-${createdAt || index}`,
    chatId: message?.ChatId || message?.chatId || null,
    projectId: message?.ProjectId || message?.projectId || null,
    sessionId: extractSandboxSessionId(message),
    role: normalizeMessageRole(message?.Role || message?.role),
    provider: message?.Provider || message?.provider || null,
    model: message?.Model || message?.model || null,
    message: displayMessage,
    content: displayMessage,
    imageUrls: normalizeImageUrls(message),
    files,
    sandbox,
    rawContent: message?.RawContent || message?.rawContent || message?.Content || message?.content || '',
    suggestions: normalizeSuggestions(message?.Suggestions || message?.suggestions),
    feedback: message?.Feedback || message?.feedback || null,
    createdAt: createdAt || new Date().toISOString()
  };
};

const roleSortRank = (role) => {
  if (role === 'user') return 0;
  if (role === 'agent-activity') return 1;
  if (role === 'assistant') return 2;
  return 3;
};

const sortMessagesByCreatedAt = (messages) =>
  [...messages].sort((left, right) => {
    const leftTime = new Date(left.createdAt || 0).getTime();
    const rightTime = new Date(right.createdAt || 0).getTime();
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }

    const roleDiff = roleSortRank(left.role) - roleSortRank(right.role);
    if (roleDiff !== 0) {
      return roleDiff;
    }

    return String(left.id || '').localeCompare(String(right.id || ''));
  });

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || '');

const buildSseEvent = (rawEvent) => {
  const lines = rawEvent.split(/\r?\n/);
  let eventName = '';
  const dataLines = [];

  lines.forEach((line) => {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
      return;
    }

    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  });

  return {
    eventName,
    data: dataLines.join('\n')
  };
};

const parseEventPayload = (data, fallbackMessage) => {
  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data);
  } catch (error) {
    if (fallbackMessage) {
      throw new Error(fallbackMessage);
    }
    return null;
  }
};

const extractHtmlFromFiles = (files = {}) => {
  const entries = Object.entries(files);
  const preferred = ['index.html', 'preview.html', 'public/index.html'].find((fileName) => files[fileName]);

  if (preferred) {
    return files[preferred];
  }

  const htmlEntry = entries.find(([filePath]) => filePath.endsWith('.html'));
  return htmlEntry?.[1] || null;
};

const getResultMessage = (result) =>
  result?.message ||
  result?.Message ||
  result?.data?.message ||
  result?.data?.Message ||
  result?.Data?.message ||
  result?.Data?.Message;

const normalizeStreamResult = (result, fullText) => {
  const reply =
    getResultMessage(result) ||
    fullText ||
    'Request completed successfully.';

  const generatedHtml =
    result?.generatedHtml ||
    result?.GeneratedHtml ||
    result?.previewHtml ||
    result?.PreviewHtml ||
    extractHtmlFromFiles(result?.files) ||
    extractHtmlFromFiles(result?.sandbox?.files) ||
    null;

  const generatedFiles = extractPreviewFiles(result);

  return {
    reply,
    generatedHtml,
    generatedFiles,
    result
  };
};

const attemptSendChatMessage = async ({
  chatId,
  projectId,
  message,
  provider,
  model,
  imageUrls = [],
  signal,
  onStart,
  onDelta,
  onResult,
  onDone
}) => {
  const state = {
    fullText: '',
    finalResult: null,
    didReceiveDone: false,
    didNotifyDone: false
  };

  const { signal: requestSignal, dispose: disposeRequestSignal } = createCombinedAbortSignal(
    signal,
    LLM_REQUEST_TIMEOUT_MS
  );

  try {
    const token = getAuthToken();
    const response = await fetch(`${apiClient.defaults.baseURL}/Llm/generate`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        Provider: provider,
        ChatId: chatId,
        ProjectId: isUuid(projectId) ? projectId : undefined,
        Prompt: message,
        Model: model,
        improvePrompt: false,
        imageUrls: Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : []
      }),
      signal: requestSignal
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorized();
      }

      throw new ApiRequestError(await readFetchApiError(response), response.status);
    }

    if (!response.body) {
      throw new Error('Response body is empty.');
    }

    onStart?.('connected');

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const processEvent = (rawEvent) => {
      const { eventName, data } = buildSseEvent(rawEvent);

      if (eventName === 'start') {
        const payload = parseEventPayload(data);
        onStart?.(payload?.content || data);
        return;
      }

      if (eventName === 'delta') {
        const payload = parseEventPayload(data);
        const chunk = payload?.content || '';
        state.fullText += chunk;
        onDelta?.(chunk, state.fullText);
        return;
      }

      if (eventName === 'result') {
        state.finalResult = parseEventPayload(data, 'Failed to parse result event.');
        onResult?.(state.finalResult);
        return;
      }

      if (eventName === 'done') {
        state.didReceiveDone = true;
        if (!state.didNotifyDone) {
          state.didNotifyDone = true;
          onDone?.({ result: state.finalResult, fullText: state.fullText });
        }
      }
    };

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || '';

      events.forEach(processEvent);

      if (state.didReceiveDone) {
        break;
      }
    }

    if (!state.didReceiveDone) {
      const remaining = buffer.trim();
      if (remaining) {
        processEvent(remaining);
      }
      if (!state.didNotifyDone) {
        state.didNotifyDone = true;
        onDone?.({ result: state.finalResult, fullText: state.fullText });
      }
    }

    return { result: normalizeStreamResult(state.finalResult, state.fullText), state };
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }

    if (state.fullText || state.finalResult) {
      if (!state.didNotifyDone) {
        state.didNotifyDone = true;
        onDone?.({ result: state.finalResult, fullText: state.fullText });
      }
      return { result: normalizeStreamResult(state.finalResult, state.fullText), state };
    }

    throw error;
  } finally {
    disposeRequestSignal();
  }
};

export const sendChatMessageApi = async ({
  chatId,
  projectId,
  projectName,
  message,
  provider = DEFAULT_LLM_PROVIDER_KEY,
  model = DEFAULT_LLM_MODEL_KEY,
  imageUrls = [],
  signal,
  onStart,
  onDelta,
  onResult,
  onDone
}) => {
  if (!isUuid(chatId)) {
    throw new Error('A valid chat id is required to send a message.');
  }

  try {
    const { result } = await retryAsync(
      () =>
        attemptSendChatMessage({
          chatId,
          projectId,
          message,
          provider,
          model,
          imageUrls,
          signal,
          onStart,
          onDelta,
          onResult,
          onDone
        }),
      {
        maxAttempts: LLM_REQUEST_MAX_ATTEMPTS,
        signal,
        shouldRetry: (error) => error?.name !== 'AbortError'
      }
    );

    return result;
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn(
        getApiErrorMessage(error, `AI request failed for project "${projectName}".`)
      );
    }
    throw error;
  }
};


export const fetchChatMessagesApi = async (chatId) => {
  if (!isUuid(chatId)) {
    throw new Error('A valid chat id is required to load messages.');
  }

  try {
    const { data } = await apiClient.get(`/Llm/chats/${encodeURIComponent(chatId)}/messages`);
    return sortMessagesByCreatedAt(unwrapApiList(data).map(normalizeChatMessage));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Chat messages request failed.'));
  }
};

export const uploadProjectAttachmentApi = async ({ projectId, file }) => {
  if (!isUuid(projectId)) {
    throw new Error('A valid project id is required to upload attachment.');
  }

  if (!(file instanceof File)) {
    throw new Error('A valid file is required to upload attachment.');
  }

  const formData = new FormData();
  formData.append('File', file);

  const isConcurrencyLikeError = (error) => {
    const status = error?.response?.status;
    const message = `${error?.response?.data?.Message || error?.response?.data?.message || error?.message || ''}`.toLowerCase();
    return (
      status === 409 ||
      (status >= 500 && status < 600) ||
      message.includes('concurrency') ||
      message.includes('optimistic concurrency') ||
      message.includes('expected to affect 1 row')
    );
  };

  try {
    const { data } = await retryAsync(
      () =>
        apiClient.post(
          `/Projects/${encodeURIComponent(projectId)}/attachments`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        ),
      {
        maxAttempts: ATTACHMENT_UPLOAD_MAX_ATTEMPTS,
        shouldRetry: (error) => isConcurrencyLikeError(error)
      }
    );

    const payload = unwrapApiData(data);
    return {
      attachmentId: payload?.AttachmentId || payload?.attachmentId || null,
      fileName: payload?.FileName || payload?.fileName || file.name,
      contentType: payload?.ContentType || payload?.contentType || file.type || '',
      sizeBytes: payload?.SizeBytes || payload?.sizeBytes || file.size || 0,
      url: payload?.Url || payload?.url || ''
    };
  } catch (error) {
    const fallbackMessage = isConcurrencyLikeError(error)
      ? 'Attachment upload failed because the project state changed. Please try again.'
      : 'Attachment upload failed.';
    throw new Error(getApiErrorMessage(error, fallbackMessage));
  }
};

export const revertChatMessageApi = async (messageId) => {
  if (!messageId) {
    throw new Error('A valid message id is required to revert.');
  }

  try {
    await apiClient.post(`/Llm/messages/${encodeURIComponent(messageId)}/revert`);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to revert message.'));
  }
};
